import * as React from "react"
import { useSpring, config } from "react-spring"
import { useDrag } from "react-use-gesture"
import { disableBodyScroll, clearAllBodyScrollLocks } from "body-scroll-lock"

import clamp from "lodash/clamp"

function useShopFilter(height: number) {
	const sheetRef = React.useRef(null!)
	const draggingRef = React.useRef(false)
	const [{ y }, set] = useSpring(() => ({ y: height }))

	const open = (canceled?: boolean) => {
		// when the user opens the sheet, we disable the body from scrolling
		disableBodyScroll(sheetRef.current)
		// when cancel is true, it means that the user passed the upwards threshold
		// so we change the spring config to create a nice wobbly effect
		set({ y: 0, config: canceled ? config.wobbly : config.stiff })
	}
	const close = () => {
		// when the user opens the sheet, we enable the body scroll
		clearAllBodyScrollLocks()
		set({ y: height, config: config.stiff })
	}

	const bind = useDrag(
		({
			first,
			last,
			vxvy: [, vy],
			movement: [, my],
			memo = y.getValue(),
			cancel,
			canceled,
		}) => {
			let newY = memo + my

			if (first) draggingRef.current = true
			// if this is not the first or last frame, it's a moving frame
			// then it means the user is dragging
			else if (last) draggingRef.current = false
			// adds friction when dragging the sheet upward
			// the more the user drags up, the more friction
			if (newY < 0) newY = newY / (1 - newY * 0.005)

			// if the user drags up passed a threshold, then we cancel
			// the drag so that the sheet resets to its open position
			if (newY < -120) cancel && cancel()

			// when the user releases the sheet, we check whether it passed
			// the treshold for it to close, or if we reset it to its open positino
			if (last)
				newY > height * 0.75 || vy > 0.5 ? close() : open(canceled)
			// when the user keeps dragging, we just move the sheet according to
			// the cursor position
			else
				set({
					y: clamp(newY, -200, height),
					immediate: true,
					config: config.stiff,
				})
			return memo
		}
	)

	const display = y.to((py) => (py < height ? "block" : "none"))

	const bgStyle = {
		opacity: y.to([0, height], [0.4, 1], "clamp"),
	}

	return { display, bgStyle, bind, close, open, sheetRef, y }
}

export default useShopFilter
